/**
 * A reply's Details, for a Mauvely developer: its figures, the Worker's request
 * ids, the system prompt it was sent and each round of calls and results, as
 * the Worker kept them (lib/engine/debug.ts cuts each part to fit).
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { getReplyDebug, type ReplyDebug } from '../api';
import { useApp } from '../state';
import { dollars } from './settings/format';
import { MonoLabel, Ph } from './ui';

const pre: CSSProperties = {
  margin: 0,
  padding: '10px 12px',
  maxHeight: 260,
  overflow: 'auto',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--app-panel-sunken)',
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  lineHeight: 1.55,
  color: 'var(--text-body)',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere'
};

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 13, color: 'var(--text-strong)', overflowWrap: 'anywhere' }}>{children}</dd>
    </>
  );
}

export function DebugDialog({ chatId, messageId, onClose }: { chatId: string; messageId: string; onClose: () => void }) {
  const { catalogue } = useApp();
  const [debug, setDebug] = useState<ReplyDebug | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getReplyDebug(chatId, messageId)
      .then((d) => {
        if (!cancelled) setDebug(d);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [chatId, messageId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const s = debug?.stats;
  const model = s ? (catalogue?.models.find((m) => m.id === s.model)?.name ?? s.model) : '';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40,
        background: 'rgba(8,6,16,.45)',
        display: 'flex',
        justifyContent: 'center',
        padding: '8vh 16px'
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Details"
        className="fade-in scroll"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 720,
          maxWidth: '100%',
          maxHeight: '84vh',
          overflowY: 'auto',
          alignSelf: 'flex-start',
          background: 'var(--surface-card)',
          borderRadius: 'var(--radius-dialog)',
          boxShadow: 'var(--shadow-xl)',
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--text-strong)' }}>Details</span>
          <button
            type="button"
            className="bare hov-soft"
            aria-label="Close"
            onClick={onClose}
            style={{ width: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: 6, color: 'var(--text-muted)' }}
          >
            <Ph name="x" size={15} />
          </button>
        </div>

        {failed ? <span style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>These details did not load.</span> : null}

        {s ? (
          <dl
            className="selectable"
            style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', alignItems: 'baseline' }}
          >
            <Fact label="Model">{model}</Fact>
            <Fact label="Paid by">{s.billing === 'mauvely' ? `Mauvely · ${dollars(s.units)}` : 'Own key'}</Fact>
            <Fact label="Tokens">
              {s.tokensIn.toLocaleString('en-GB')} in · {s.tokensOut.toLocaleString('en-GB')} out
            </Fact>
            <Fact label="Time">{(s.ms / 1000).toFixed(1)} s working</Fact>
            <Fact label="Rounds">{s.rounds}</Fact>
            <Fact label="Requests">
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{s.requestIds.join(' · ')}</span>
            </Fact>
          </dl>
        ) : null}

        {debug && !debug.detail ? (
          <span style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>Only the figures were kept for this reply.</span>
        ) : null}

        {debug?.detail ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <MonoLabel>System prompt</MonoLabel>
              <pre className="selectable" style={pre}>
                {debug.detail.system}
              </pre>
            </div>
            {debug.detail.exchange.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <MonoLabel>Rounds</MonoLabel>
                {debug.detail.exchange.map((t, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      {t.role === 'tool' ? `Result of ${t.name}` : t.role === 'assistant' ? 'Assist' : 'Sent with the results'}
                    </span>
                    {t.text ? (
                      <pre className="selectable" style={pre}>
                        {t.text}
                      </pre>
                    ) : null}
                    {(t.calls ?? []).map((c, j) => (
                      <pre key={j} className="selectable" style={pre}>
                        {`${c.name}(${c.arguments})`}
                      </pre>
                    ))}
                  </div>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
